import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import LoadingSpinner from "../common/LoadingSpinner";
import Icon from "../common/Icon";
import SerpComparison from "./SerpComparison";
import OptimizationView from "./OptimizationView";
import { studiesAPI } from "../../services/api";
import { formatNumber } from "../../utils/formatters";
import { buildClusters, reclusterKeywords } from "../../utils/clusteringAlgorithm";
import { orientSimilarityPairsForColumns, isPairAllowed, buildAllowedKeywordsSet } from "../../utils/similarityPairs";
import { exportToExcel, prepareSimilaritiesForExport } from "../../utils/exportUtils";
import { useAutoSaveOnExit } from "../../hooks/useAutoSaveOnExit";
import "./SimilaritiesTable.css";

const FILTERS_STORAGE_KEY_PREFIX = 'clustaar_filters_';

const loadFiltersFromStorage = (studyId) => {
  try {
    const stored = localStorage.getItem(`${FILTERS_STORAGE_KEY_PREFIX}${studyId}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Erreur lors du chargement des filtres:', e);
  }
  return null;
};

const saveFiltersToStorage = (studyId, filters) => {
  try {
    localStorage.setItem(`${FILTERS_STORAGE_KEY_PREFIX}${studyId}`, JSON.stringify(filters));
  } catch (e) {
    console.error('Erreur lors de la sauvegarde des filtres:', e);
  }
};

const cleanUrl = (url) => {
  if (!url) return '-';
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    params.delete('srsltid');
    
    const cleanedSearch = params.toString();
    let cleanedUrl = `${urlObj.protocol}//${urlObj.host}${decodeURIComponent(urlObj.pathname)}`;
    
    if (cleanedSearch) {
      cleanedUrl += `?${cleanedSearch}`;
    }
    return cleanedUrl;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to parse URL:', url, error);
    }
    return url;
  }
};

const SimilaritiesTable = ({ studyId, keywords = [] }) => {
  const [similarities, setSimilarities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState("keywords");
  const [sortConfig, setSortConfig] = useState([
    { key: "similarityScore", direction: "desc" }
  ]);
  const [searchTerm1, setSearchTerm1] = useState("");
  const [searchTerm2, setSearchTerm2] = useState("");
  const [isDropdown1Open, setIsDropdown1Open] = useState(false);
  const [isDropdown2Open, setIsDropdown2Open] = useState(false);
  const [excludedKeywords1, setExcludedKeywords1] = useState([]);
  const [excludedKeywords2, setExcludedKeywords2] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [similarityThreshold, setSimilarityThreshold] = useState(() => {
    const stored = loadFiltersFromStorage(studyId);
    return stored?.similarityThreshold ?? 50;
  });
  const [expandedClusters, setExpandedClusters] = useState(new Set()); // Set<mainKeyword>
  const [manualClusters, setManualClusters] = useState([]);
  const [addingToCluster, setAddingToCluster] = useState(null);
  const [keywordSearchTerm, setKeywordSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isCreatingCluster, setIsCreatingCluster] = useState(false);
  const [newClusterSearchTerm, setNewClusterSearchTerm] = useState('');
  const [moveKeywordModal, setMoveKeywordModal] = useState(null);
  const [moveClusterSearchTerm, setMoveClusterSearchTerm] = useState('');
  const [savingClusters, setSavingClusters] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [createClusterFromOrphanModal, setCreateClusterFromOrphanModal] = useState(null);
  const [orphanClusterSearchTerm, setOrphanClusterSearchTerm] = useState('');
  const [selectedKeywordsForCluster, setSelectedKeywordsForCluster] = useState([]);
  const [clusterListSearchTerm, setClusterListSearchTerm] = useState('');
  const [isManualMode, setIsManualMode] = useState(false);
  const [deletedOrphans, setDeletedOrphans] = useState(new Set());
  const [showDeletedKeywords, setShowDeletedKeywords] = useState(false);
  const [mergeClusterModal, setMergeClusterModal] = useState(null);
  const [mergeClusterSearchTerm, setMergeClusterSearchTerm] = useState('');
  const [selectedMergeClusters, setSelectedMergeClusters] = useState(new Set());
  const [clusterPage, setClusterPage] = useState(1);
  const clustersPerPage = 100;
  
  const storedFilters = useMemo(() => loadFiltersFromStorage(studyId), [studyId]);
  const [activeFilters, setActiveFilters] = useState(() => storedFilters?.activeFilters || []);
  const [filterIdCounter, setFilterIdCounter] = useState(() => {
    const maxId = (storedFilters?.activeFilters || []).reduce((max, f) => Math.max(max, f.id || 0), 0);
    return maxId + 1;
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [filterDropdownSearch, setFilterDropdownSearch] = useState('');
  const [expandedFilterCategories, setExpandedFilterCategories] = useState(new Set());
  const [filterConfigModal, setFilterConfigModal] = useState(null);
  const [pendingFilterConfig, setPendingFilterConfig] = useState({ operator: '>', value: '', value2: '' });
  const filterDropdownRef = useRef(null);
  
  const [regexFilters, setRegexFilters] = useState(() => storedFilters?.regexFilters || {
    regexInclude: '',
    regexExclude: ''
  });
  const [regexErrors, setRegexErrors] = useState({ include: '', exclude: '' });

  const compiledRegexInclude = useMemo(() => {
    if (!regexFilters.regexInclude.trim() || regexErrors.include) return null;
    try {
      return new RegExp(regexFilters.regexInclude, 'i');
    } catch {
      return null;
    }
  }, [regexFilters.regexInclude, regexErrors.include]);

  const compiledRegexExclude = useMemo(() => {
    if (!regexFilters.regexExclude.trim() || regexErrors.exclude) return null;
    try {
      return new RegExp(regexFilters.regexExclude, 'i');
    } catch {
      return null;
    }
  }, [regexFilters.regexExclude, regexErrors.exclude]);

  const isInitialMount = useRef(true);
  const thresholdDebounceTimer = useRef(null);
  const hasLoadedSavedClusters = useRef(false);
  const isLoadingInitialData = useRef(true);
  const previousViewRef = useRef("keywords");
  const isMountedRef = useRef(true);
  const pageSize = 100;

  useEffect(() => {
    saveFiltersToStorage(studyId, {
      activeFilters,
      regexFilters,
      similarityThreshold
    });
  }, [studyId, activeFilters, regexFilters, similarityThreshold]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        isLoadingInitialData.current = true;
        
        const localFilters = loadFiltersFromStorage(studyId);
        const hasLocalThreshold = localFilters?.similarityThreshold !== undefined;
        
        try {
          const savedData = await studiesAPI.getClusters(studyId);
          if (savedData?.clustersData) {
            if (!hasLocalThreshold) {
              const savedThreshold = savedData.clustersData.threshold ?? 50;
              setSimilarityThreshold(savedThreshold);
            }
            
            if (savedData.clustersData.excludedKeywords?.length > 0) {
              const excludedSet = new Set(savedData.clustersData.excludedKeywords.map(kw => kw.keyword));
              setDeletedOrphans(excludedSet);
            }
            
            hasLoadedSavedClusters.current = true;
          }
        } catch (clusterErr) {
          hasLoadedSavedClusters.current = false;
        }
        
        const data = await studiesAPI.getSimilarities(studyId);
        setSimilarities(data.similarities || []);
        
        isLoadingInitialData.current = false;
      } catch (err) {
        setError(err.message);
        isLoadingInitialData.current = false;
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studyId]);

  useEffect(() => {
    setExcludedKeywords1([]);
    setExcludedKeywords2([]);
    setCurrentPage(1);
  }, [keywords]);

  useEffect(() => {
    const handleScroll = (e) => {
      if (addingToCluster !== null) {
        const target = e.target;
        const isScrollInsideDropdown = target && typeof target.closest === 'function' && target.closest('.dropdown-menu-inline');
        if (isScrollInsideDropdown) {
          return;
        }
        setAddingToCluster(null);
        setKeywordSearchTerm('');
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [addingToCluster]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && moveKeywordModal) {
        setMoveKeywordModal(null);
        setMoveClusterSearchTerm('');
      }
      if (e.key === 'Escape' && createClusterFromOrphanModal) {
        setCreateClusterFromOrphanModal(null);
        setOrphanClusterSearchTerm('');
      }
      if (e.key === 'Escape' && mergeClusterModal) {
        setMergeClusterModal(null);
        setMergeClusterSearchTerm('');
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [moveKeywordModal, createClusterFromOrphanModal, mergeClusterModal]);

  useEffect(() => {
    if (!moveKeywordModal) {
      setMoveClusterSearchTerm('');
    }
  }, [moveKeywordModal]);

  useEffect(() => {
    if (!mergeClusterModal) {
      setMergeClusterSearchTerm('');
    }
  }, [mergeClusterModal]);

  useEffect(() => {
    if (!createClusterFromOrphanModal) {
      setOrphanClusterSearchTerm('');
      setSelectedKeywordsForCluster([]);
    } else if (createClusterFromOrphanModal.action === 'create') {
      setSelectedKeywordsForCluster([createClusterFromOrphanModal.orphanKeyword]);
    }
  }, [createClusterFromOrphanModal]);

  const allowedKeywordsSet = useMemo(() => buildAllowedKeywordsSet(keywords), [keywords]);

  const allKeywords1 = useMemo(() => {
    const keywordSet = new Set();
    similarities.forEach((sim) => {
      if (allowedKeywordsSet.size === 0 || allowedKeywordsSet.has(sim.keyword1.toLowerCase())) {
        keywordSet.add(sim.keyword1);
      }
      if (allowedKeywordsSet.size === 0 || allowedKeywordsSet.has(sim.keyword2.toLowerCase())) {
        keywordSet.add(sim.keyword2);
      }
    });
    return Array.from(keywordSet).sort();
  }, [similarities, allowedKeywordsSet]);

  const allKeywords2 = useMemo(() => {
    const keywordSet = new Set();
    similarities.forEach((sim) => {
      if (allowedKeywordsSet.size === 0 || allowedKeywordsSet.has(sim.keyword1.toLowerCase())) {
        keywordSet.add(sim.keyword1);
      }
      if (allowedKeywordsSet.size === 0 || allowedKeywordsSet.has(sim.keyword2.toLowerCase())) {
        keywordSet.add(sim.keyword2);
      }
    });
    return Array.from(keywordSet).sort();
  }, [similarities, allowedKeywordsSet]);

  const filteredDropdownKeywords1 = useMemo(() => {
    if (!searchTerm1) return allKeywords1;
    return allKeywords1.filter((kw) =>
      kw.toLowerCase().includes(searchTerm1.toLowerCase())
    );
  }, [allKeywords1, searchTerm1]);

  const filteredDropdownKeywords2 = useMemo(() => {
    if (!searchTerm2) return allKeywords2;
    return allKeywords2.filter((kw) =>
      kw.toLowerCase().includes(searchTerm2.toLowerCase())
    );
  }, [allKeywords2, searchTerm2]);

  const toggleKeyword1 = (keyword) => {
    setExcludedKeywords1((prev) =>
      prev.includes(keyword)
        ? prev.filter((k) => k !== keyword)
        : [...prev, keyword]
    );
  };

  const toggleKeyword2 = (keyword) => {
    setExcludedKeywords2((prev) =>
      prev.includes(keyword)
        ? prev.filter((k) => k !== keyword)
        : [...prev, keyword]
    );
  };

  const excludeAll1 = () => setExcludedKeywords1([...allKeywords1]);
  const excludeAll2 = () => setExcludedKeywords2([...allKeywords2]);
  const includeAll1 = () => setExcludedKeywords1([]);
  const includeAll2 = () => setExcludedKeywords2([]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const existingIndex = prev.findIndex((s) => s.key === key);
      
      if (existingIndex === -1) {
        return [...prev, { key, direction: "asc" }];
      }
      
      const existing = prev[existingIndex];
      if (existing.direction === "asc") {
        const newConfig = [...prev];
        newConfig[existingIndex] = { key, direction: "desc" };
        return newConfig;
      }
      
      return prev.filter((s) => s.key !== key);
    });
  };

  const getSortIndicator = (key) => {
    const config = sortConfig.find((s) => s.key === key);
    if (!config) return null;
    
    const index = sortConfig.findIndex((s) => s.key === key);
    const arrow = config.direction === "asc" ? "↑" : "↓";
    
    return (
      <span className="sort-indicator">
        {arrow}
        {sortConfig.length > 1 && <sup>{index + 1}</sup>}
      </span>
    );
  };

  const applyOperatorFilter = useCallback((value, operator, filterVal, filterVal2) => {
    if (filterVal === '' || filterVal === undefined) return true;
    const numVal = parseFloat(filterVal);
    const numVal2 = filterVal2 ? parseFloat(filterVal2) : null;
    if (isNaN(numVal)) return true;
    
    switch (operator) {
      case '>': return value > numVal;
      case '<': return value < numVal;
      case '=': return value === numVal;
      case '>=': return value >= numVal;
      case '<=': return value <= numVal;
      case 'between': return numVal2 !== null && !isNaN(numVal2) ? value >= numVal && value <= numVal2 : true;
      default: return true;
    }
  }, []);

  const sortedSimilarities = useMemo(() => {
    let filtered = orientSimilarityPairsForColumns(similarities, excludedKeywords1, excludedKeywords2, allowedKeywordsSet);

    const keywordFilters = activeFilters.filter(f => f.category === 'keyword' && f.value !== '');
    keywordFilters.forEach(filter => {
      filtered = filtered.filter(sim => {
        let val1, val2;
        switch(filter.field) {
          case 'volume':
            val1 = sim.volume1 || 0;
            val2 = sim.volume2 || 0;
            break;
          case 'position':
            val1 = sim.position1;
            val2 = sim.position2;
            break;
          case 'traffic':
            val1 = sim.estimatedTraffic1 || 0;
            val2 = sim.estimatedTraffic2 || 0;
            break;
          default:
            return true;
        }
        const match1 = filter.field === 'position' && !val1 ? false : applyOperatorFilter(val1, filter.operator, filter.value, filter.value2);
        const match2 = filter.field === 'position' && !val2 ? false : applyOperatorFilter(val2, filter.operator, filter.value, filter.value2);
        return match1 || match2;
      });
    });

    if (regexFilters.regexInclude.trim() && !regexErrors.include && compiledRegexInclude) {
      filtered = filtered.filter(sim => compiledRegexInclude.test(sim.keyword1) || compiledRegexInclude.test(sim.keyword2));
    }

    if (regexFilters.regexExclude.trim() && !regexErrors.exclude && compiledRegexExclude) {
      filtered = filtered.filter(sim => !compiledRegexExclude.test(sim.keyword1) && !compiledRegexExclude.test(sim.keyword2));
    }

    if (sortConfig.length === 0) return filtered;

    return [...filtered].sort((a, b) => {
      for (const { key, direction } of sortConfig) {
        let aVal = a[key];
        let bVal = b[key];

        if (typeof aVal === "string") {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return direction === "asc" ? -1 : 1;
        if (aVal > bVal) return direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [similarities, excludedKeywords1, excludedKeywords2, sortConfig, activeFilters, regexFilters, regexErrors, compiledRegexInclude, compiledRegexExclude, applyOperatorFilter, allowedKeywordsSet]);

  const totalPages = Math.ceil(sortedSimilarities.length / pageSize);
  const paginatedSimilarities = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedSimilarities.slice(start, start + pageSize);
  }, [sortedSimilarities, currentPage, pageSize]);

  const exportablePairs = useMemo(() => {
    return sortedSimilarities.filter((sim) => sim.similarityScore > 0);
  }, [sortedSimilarities]);

  const handleExportSimilarities = () => {
    const nowIso = new Date().toISOString();
    const dateStr = nowIso.split('T')[0];
    const exportRows = prepareSimilaritiesForExport(exportablePairs);
    const sheets = [{ name: 'Similarites', data: exportRows }];
    exportToExcel(sheets, `similarites_${studyId}_${dateStr}`);
  };

  const { clusters, orphans } = useMemo(() => {
    const keywordsWithVolume = keywords.filter(kw => kw.volume > 0);
    
    if (isManualMode) {
      const filteredClusters = manualClusters;
      
      const keywordsInClusters = new Set();
      filteredClusters.forEach(cluster => {
        keywordsInClusters.add(cluster.mainKeyword);
        cluster.keywords.forEach(kw => keywordsInClusters.add(kw.keyword));
      });
      
      const orphans = keywordsWithVolume
        .filter(kw => !keywordsInClusters.has(kw.keyword))
        .map(kw => ({
          keyword: kw.keyword,
          volume: kw.volume || 0,
          position: kw.position,
          estimatedTraffic: kw.estimatedTraffic || 0,
          url: kw.url
        }))
        .sort((a, b) => b.volume - a.volume);
      
      return { clusters: filteredClusters, orphans };
    }
    
    const thresholdFiltered = similarities.filter(s => s.similarityScore >= similarityThreshold);
    const categoryFiltered = thresholdFiltered.filter(sim => isPairAllowed(sim, allowedKeywordsSet));
    return buildClusters(categoryFiltered, keywordsWithVolume);
  }, [similarities, similarityThreshold, keywords, manualClusters, isManualMode, allowedKeywordsSet]);

  const clusteredKeywords = useMemo(() => {
    const set = new Set();
    clusters.forEach(cluster => {
      set.add(cluster.mainKeyword);
      cluster.keywords.forEach(kw => set.add(kw.keyword));
    });
    return set;
  }, [clusters]);

  const orphansAsClusters = useMemo(() => {
    return orphans
      .filter(orphan => !deletedOrphans.has(orphan.keyword))
      .map(orphan => ({
        id: `orphan-${orphan.keyword}`,
        mainKeyword: orphan.keyword,
        mainVolume: orphan.volume,
        mainPosition: orphan.position,
        mainUrl: orphan.url,
        mainTraffic: orphan.estimatedTraffic || 0,
        keywords: [],
        totalVolume: orphan.volume,
        totalTraffic: orphan.estimatedTraffic || 0,
        keywordCount: 1,
        averageSimilarity: 100,
        isOrphan: true
      }));
  }, [orphans, deletedOrphans]);

  const allClustersForDisplay = useMemo(() => {
    return [...clusters, ...orphansAsClusters];
  }, [clusters, orphansAsClusters]);

  const filterTypeConfig = useMemo(() => ({
    cluster: {
      volume: { label: 'Volume total', getValue: (c) => c.totalVolume || 0 },
      keywordCount: { label: 'Nb mots-clés', getValue: (c) => c.keywordCount || 0 },
      position: { label: 'Position', getValue: (c) => c.mainPosition },
      traffic: { label: 'Trafic', getValue: (c) => c.totalTraffic || 0 }
    },
    keyword: {
      volume: { label: 'Volume', getValue: (kw) => kw.volume || 0 },
      position: { label: 'Position', getValue: (kw) => kw.position },
      traffic: { label: 'Trafic', getValue: (kw) => kw.estimatedTraffic || 0 }
    }
  }), []);

  const filteredClustersForDisplay = useMemo(() => {
    let result = allClustersForDisplay;
    
    if (clusterListSearchTerm.trim()) {
      const term = clusterListSearchTerm.toLowerCase();
      result = result.filter(c =>
        c.mainKeyword?.toLowerCase().includes(term) ||
        c.keywords?.some(kw => kw.keyword?.toLowerCase().includes(term))
      );
    }
    
    const clusterTypeFilters = activeFilters.filter(f => f.category === 'cluster' && f.value !== '');
    const keywordTypeFilters = activeFilters.filter(f => f.category === 'keyword' && f.value !== '');
    
    clusterTypeFilters.forEach(filter => {
      const config = filterTypeConfig.cluster[filter.field];
      if (config) {
        result = result.filter(c => {
          const val = config.getValue(c);
          if (filter.field === 'position' && !val) return false;
          return applyOperatorFilter(val, filter.operator, filter.value, filter.value2);
        });
      }
    });
    
    if (regexFilters.regexInclude.trim() && !regexErrors.include && compiledRegexInclude) {
      result = result.filter(c => 
        compiledRegexInclude.test(c.mainKeyword) || 
        c.keywords?.some(kw => compiledRegexInclude.test(kw.keyword))
      );
    }
    
    if (regexFilters.regexExclude.trim() && !regexErrors.exclude && compiledRegexExclude) {
      result = result.filter(c => 
        !compiledRegexExclude.test(c.mainKeyword) && 
        !c.keywords?.some(kw => compiledRegexExclude.test(kw.keyword))
      );
    }
    
    if (keywordTypeFilters.length > 0) {
      result = result.map(cluster => {
        const matchesFilter = (kw) => {
          for (const filter of keywordTypeFilters) {
            const config = filterTypeConfig.keyword[filter.field];
            if (config) {
              const val = config.getValue(kw);
              if (filter.field === 'position' && !val) return false;
              if (!applyOperatorFilter(val, filter.operator, filter.value, filter.value2)) return false;
            }
          }
          return true;
        };
        
        const mainMatches = matchesFilter({ 
          volume: cluster.mainVolume, 
          position: cluster.mainPosition, 
          estimatedTraffic: cluster.mainTraffic 
        });
        
        const filteredKeywords = cluster.keywords?.filter(kw => matchesFilter(kw)) || [];
        
        if (!mainMatches && filteredKeywords.length === 0) return null;
        
        return {
          ...cluster,
          keywords: filteredKeywords,
          keywordCount: (mainMatches ? 1 : 0) + filteredKeywords.length,
          totalVolume: (mainMatches ? cluster.mainVolume : 0) + filteredKeywords.reduce((sum, k) => sum + (k.volume || 0), 0),
          totalTraffic: (mainMatches ? cluster.mainTraffic : 0) + filteredKeywords.reduce((sum, k) => sum + (k.estimatedTraffic || 0), 0)
        };
      }).filter(Boolean);
    }
    
    return result;
  }, [allClustersForDisplay, clusterListSearchTerm, activeFilters, regexFilters, regexErrors, compiledRegexInclude, compiledRegexExclude, applyOperatorFilter, filterTypeConfig]);

  const clusterIndexMap = useMemo(() => {
    const map = new Map();
    clusters.forEach((c, i) => map.set(c.mainKeyword, i));
    return map;
  }, [clusters]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredClustersForDisplay.length / clustersPerPage));
    if (clusterPage > totalPages) {
      setClusterPage(totalPages);
    }
  }, [filteredClustersForDisplay.length, clusterPage, clustersPerPage]);

  useEffect(() => {
    if (isLoadingInitialData.current) {
      return;
    }

    if (similarities.length === 0) return;

    if (thresholdDebounceTimer.current) {
      clearTimeout(thresholdDebounceTimer.current);
    }

    thresholdDebounceTimer.current = setTimeout(() => {
      const thresholdFiltered = similarities.filter(s => s.similarityScore >= similarityThreshold);
      const categoryFiltered = thresholdFiltered.filter(sim => isPairAllowed(sim, allowedKeywordsSet));
      const built = buildClusters(categoryFiltered, keywords);

      setManualClusters(built.clusters.length > 0 ? structuredClone(built.clusters) : []);
      setIsManualMode(true);
    }, 500);

    return () => {
      if (thresholdDebounceTimer.current) {
        clearTimeout(thresholdDebounceTimer.current);
      }
    };
  }, [similarities, similarityThreshold, keywords, allowedKeywordsSet]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isInitialMount.current = true;
    };
  }, []);

  const saveClustersToServer = useCallback(async () => {
    if (manualClusters.length === 0 && orphans.length === 0) return;
    
    try {
      if (isMountedRef.current) setSavingClusters(true);
      
      const clustersWithIds = manualClusters.map(cluster => ({
        ...cluster,
        id: cluster.id || `cluster-${cluster.mainKeyword}`
      }));
      
      const orphanClusters = orphans.map(orphan => ({
        id: `orphan-${orphan.keyword}`,
        mainKeyword: orphan.keyword,
        mainVolume: orphan.volume,
        mainPosition: orphan.position,
        mainUrl: orphan.url,
        mainTraffic: orphan.estimatedTraffic,
        targetUrl: '',
        keywords: [],
        totalVolume: orphan.volume,
        totalTraffic: orphan.estimatedTraffic,
        keywordCount: 1,
        averageSimilarity: 100
      }));
      
      const allClusters = [...clustersWithIds, ...orphanClusters];
      
      const excludedKeywordsList = [...deletedOrphans].map(keyword => {
        const kwData = keywords.find(k => k.keyword === keyword);
        return {
          keyword,
          volume: kwData?.volume || 0,
          position: kwData?.position || null,
          url: kwData?.url || null,
          estimatedTraffic: kwData?.estimatedTraffic || 0
        };
      });
              
      await studiesAPI.saveClusters(studyId, {
        threshold: similarityThreshold,
        clusters: allClusters,
        excludedKeywords: excludedKeywordsList,
        savedFilters: {
          activeFilters: activeFilters,
          regexFilters: regexFilters
        }
      });
      
      if (isMountedRef.current) {
        setSaveSuccess(true);
        setTimeout(() => {
          if (isMountedRef.current) setSaveSuccess(false);
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to save clusters:', err);
    } finally {
      if (isMountedRef.current) setSavingClusters(false);
    }
  }, [manualClusters, orphans, similarityThreshold, studyId, deletedOrphans, keywords, activeFilters, regexFilters]);

  const { markDirty, save: triggerSave, saveAndWait, isSaving: autoSaveInProgress } = useAutoSaveOnExit(
    saveClustersToServer,
    activeView === "clustering",
    30000
  );

  const [viewChangePending, setViewChangePending] = useState(null);

  const handleViewChange = useCallback(async (newView) => {
    if (activeView === "clustering" && newView !== "clustering") {
      setViewChangePending(newView);
      await saveAndWait();
      setViewChangePending(null);
    }
    setActiveView(newView);
  }, [activeView, saveAndWait]);

  useEffect(() => {
    const prevView = previousViewRef.current;
    previousViewRef.current = activeView;
    
    if (prevView === "clustering" && activeView !== "clustering") {
      triggerSave();
    }
  }, [activeView, triggerSave]);

  const convertOrphanToCluster = useCallback((orphanKeyword) => {
    const orphanData = orphans.find(o => o.keyword === orphanKeyword);
    if (!orphanData) return -1;
    
    const newCluster = {
      id: `cluster-${orphanKeyword}`,
      mainKeyword: orphanKeyword,
      mainVolume: orphanData.volume,
      mainPosition: orphanData.position,
      mainUrl: orphanData.url,
      mainTraffic: orphanData.estimatedTraffic || 0,
      keywords: [],
      totalVolume: orphanData.volume,
      totalTraffic: orphanData.estimatedTraffic || 0,
      keywordCount: 1,
      averageSimilarity: 100,
      customThreshold: null
    };
    
    setManualClusters(prev => [...prev, newCluster]);
    setIsManualMode(true);
    markDirty();
    return manualClusters.length;
  }, [orphans, manualClusters.length, markDirty]);

  const recalculateClusterMetrics = (cluster) => {
    if (cluster.keywords.length === 0) {
      cluster.averageSimilarity = 0;
    } else {
      const totalSimilarity = cluster.keywords.reduce((sum, k) => sum + (k.similarityScore || 0), 0);
      cluster.averageSimilarity = Math.round(totalSimilarity / cluster.keywords.length);
    }
    cluster.keywordCount = cluster.keywords.length + 1;
    cluster.totalVolume = cluster.mainVolume + cluster.keywords.reduce((sum, k) => sum + k.volume, 0);
    cluster.totalTraffic = cluster.mainTraffic + cluster.keywords.reduce((sum, k) => sum + (k.estimatedTraffic || 0), 0);
  };

  const validateRegex = (pattern) => {
    if (!pattern.trim()) return '';
    try {
      new RegExp(pattern);
      return '';
    } catch (e) {
      return 'Expression régulière invalide';
    }
  };

  const filterMenuConfig = useMemo(() => ({
    clusters: {
      label: 'Clusters',
      fields: [
        { field: 'volume', label: 'Volume total' },
        { field: 'keywordCount', label: 'Nb mots-clés' },
        { field: 'position', label: 'Position' },
        { field: 'traffic', label: 'Trafic' }
      ]
    },
    keywords: {
      label: 'Mots-clés',
      fields: [
        { field: 'volume', label: 'Volume' },
        { field: 'position', label: 'Position' },
        { field: 'traffic', label: 'Trafic' }
      ]
    },
    regex: {
      label: 'Regex',
      fields: [
        { field: 'regexInclude', label: 'Inclure (regex)' },
        { field: 'regexExclude', label: 'Exclure (regex)' }
      ]
    }
  }), []);

  const toggleFilterCategory = (category) => {
    setExpandedFilterCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const openFilterConfig = (category, field, label, editFilter = null) => {
    setFilterConfigModal({ category, field, label, editId: editFilter?.id || null });
    setPendingFilterConfig({ 
      operator: editFilter?.operator || '>', 
      value: editFilter?.value || '', 
      value2: editFilter?.value2 || '' 
    });
    setFilterDropdownOpen(false);
    setFilterDropdownSearch('');
  };

  const editExistingFilter = (filter) => {
    const catConfig = filter.category === 'cluster' ? filterTypeConfig.cluster : filterTypeConfig.keyword;
    const label = catConfig[filter.field]?.label || filter.field;
    const category = filter.category === 'cluster' ? 'clusters' : 'keywords';
    openFilterConfig(category, filter.field, label, filter);
  };

  const editRegexFilter = (field) => {
    const label = field === 'regexInclude' ? 'Inclure (regex)' : 'Exclure (regex)';
    setFilterConfigModal({ category: 'regex', field, label, editId: field });
    setPendingFilterConfig({ 
      operator: '>', 
      value: regexFilters[field], 
      value2: '' 
    });
  };

  const applyFilterConfig = () => {
    if (!filterConfigModal) return;
    const { category, field, editId } = filterConfigModal;
    
    if (category === 'regex') {
      setRegexFilters(prev => ({ ...prev, [field]: pendingFilterConfig.value }));
      if (field === 'regexInclude') {
        setRegexErrors(prev => ({ ...prev, include: validateRegex(pendingFilterConfig.value) }));
      } else {
        setRegexErrors(prev => ({ ...prev, exclude: validateRegex(pendingFilterConfig.value) }));
      }
    } else if (editId) {
      setActiveFilters(prev => prev.map(f => 
        f.id === editId 
          ? { ...f, operator: pendingFilterConfig.operator, value: pendingFilterConfig.value, value2: pendingFilterConfig.value2 }
          : f
      ));
    } else {
      const newFilter = {
        id: filterIdCounter,
        category: category === 'clusters' ? 'cluster' : 'keyword',
        field,
        operator: pendingFilterConfig.operator,
        value: pendingFilterConfig.value,
        value2: pendingFilterConfig.value2
      };
      setActiveFilters(prev => [...prev, newFilter]);
      setFilterIdCounter(prev => prev + 1);
    }
    
    setFilterConfigModal(null);
    setClusterPage(1);
  };

  const removeFilter = (filterId) => {
    setActiveFilters(prev => prev.filter(f => f.id !== filterId));
    setClusterPage(1);
  };

  const removeRegexFilter = (field) => {
    setRegexFilters(prev => ({ ...prev, [field]: '' }));
    if (field === 'regexInclude') {
      setRegexErrors(prev => ({ ...prev, include: '' }));
    } else {
      setRegexErrors(prev => ({ ...prev, exclude: '' }));
    }
    setClusterPage(1);
  };

  const resetAllFilters = () => {
    setActiveFilters([]);
    setRegexFilters({ regexInclude: '', regexExclude: '' });
    setRegexErrors({ include: '', exclude: '' });
    setClusterPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setFilterDropdownOpen(false);
        setFilterDropdownSearch('');
      }
    };
    if (filterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterDropdownOpen]);

  const createNewCluster = (selectedKeyword) => {
    const keywordData = keywords.find(k => k.keyword === selectedKeyword);
    
    const newCluster = {
      mainKeyword: selectedKeyword,
      mainVolume: keywordData?.volume || 0,
      mainPosition: keywordData?.position || null,
      mainUrl: keywordData?.url || null,
      mainTraffic: keywordData?.estimatedTraffic || 0,
      keywords: [],
      totalVolume: keywordData?.volume || 0,
      totalTraffic: keywordData?.estimatedTraffic || 0,
      keywordCount: 1,
      averageSimilarity: 0,
      isNew: true,
      customThreshold: null
    };
    setManualClusters([newCluster, ...manualClusters]);
    setIsManualMode(true);
    setExpandedClusters(prev => new Set([...prev, selectedKeyword]));
    setIsCreatingCluster(false);
    setNewClusterSearchTerm('');
    
    if (deletedOrphans.has(selectedKeyword)) {
      setDeletedOrphans(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedKeyword);
        return newSet;
      });
    }
    
    markDirty();
  };

  const createClusterFromMultipleKeywords = () => {
    if (selectedKeywordsForCluster.length < 2) return;

    const keywordsData = selectedKeywordsForCluster
      .map(kw => keywords.find(k => k.keyword === kw))
      .filter(Boolean)
      .sort((a, b) => (b.volume || 0) - (a.volume || 0));

    if (keywordsData.length === 0) return;

    const mainKeywordData = keywordsData[0];
    const otherKeywords = keywordsData.slice(1);

    const clusterKeywords = otherKeywords.map(kwData => {
      const similarity = similarities.find(sim =>
        (sim.keyword1 === mainKeywordData.keyword && sim.keyword2 === kwData.keyword) ||
        (sim.keyword2 === mainKeywordData.keyword && sim.keyword1 === kwData.keyword)
      );

      return {
        keyword: kwData.keyword,
        volume: kwData.volume || 0,
        position: kwData.position,
        estimatedTraffic: kwData.estimatedTraffic || 0,
        url: kwData.url,
        similarityScore: similarity ? Math.round(similarity.similarityScore) : 0
      };
    });

    const totalVolume = keywordsData.reduce((sum, k) => sum + (k.volume || 0), 0);
    const totalTraffic = keywordsData.reduce((sum, k) => sum + (k.estimatedTraffic || 0), 0);
    const avgSimilarity = clusterKeywords.length > 0
      ? Math.round(clusterKeywords.reduce((sum, k) => sum + k.similarityScore, 0) / clusterKeywords.length)
      : 0;

    const newCluster = {
      mainKeyword: mainKeywordData.keyword,
      mainVolume: mainKeywordData.volume || 0,
      mainPosition: mainKeywordData.position,
      mainUrl: mainKeywordData.url,
      mainTraffic: mainKeywordData.estimatedTraffic || 0,
      keywords: clusterKeywords,
      totalVolume,
      totalTraffic,
      keywordCount: keywordsData.length,
      averageSimilarity: avgSimilarity,
      isNew: true,
      customThreshold: null
    };

    setManualClusters([newCluster, ...manualClusters]);
    setIsManualMode(true);
    setExpandedClusters(prev => new Set([...prev, newCluster.mainKeyword]));
    setCreateClusterFromOrphanModal(null);
    setOrphanClusterSearchTerm('');
    setSelectedKeywordsForCluster([]);
    markDirty();
  };

  const toggleKeywordSelection = (keyword) => {
    setSelectedKeywordsForCluster(prev => {
      if (prev.includes(keyword)) {
        return prev.filter(k => k !== keyword);
      } else {
        return [...prev, keyword];
      }
    });
  };

  const addOrphanToExistingCluster = (orphanKeyword, targetClusterMainKeyword) => {
    const clusterIndex = manualClusters.findIndex(c => c.mainKeyword === targetClusterMainKeyword);
    if (clusterIndex === -1) return;

    const orphanData = keywords.find(k => k.keyword === orphanKeyword);
    if (!orphanData) return;

    const similarity = similarities.find(sim =>
      (sim.keyword1 === targetClusterMainKeyword && sim.keyword2 === orphanKeyword) ||
      (sim.keyword2 === targetClusterMainKeyword && sim.keyword1 === orphanKeyword)
    );

    const newClusters = [...manualClusters];
    const cluster = newClusters[clusterIndex];

    cluster.keywords = [...cluster.keywords, {
      keyword: orphanKeyword,
      volume: orphanData.volume || 0,
      position: orphanData.position,
      estimatedTraffic: orphanData.estimatedTraffic || 0,
      url: orphanData.url,
      similarityScore: similarity ? Math.round(similarity.similarityScore) : 0
    }];

    recalculateClusterMetrics(cluster);
    setManualClusters(newClusters);
    setCreateClusterFromOrphanModal(null);
    setOrphanClusterSearchTerm('');
    markDirty();
  };

  const deleteCluster = (clusterIndex) => {
    const clusterToDelete = manualClusters[clusterIndex];
    if (!clusterToDelete) return;
    
    setManualClusters(manualClusters.filter((_, idx) => idx !== clusterIndex));
    setExpandedClusters(prev => {
      const newSet = new Set(prev);
      newSet.delete(clusterToDelete.mainKeyword);
      return newSet;
    });
    markDirty();
  };

  const deleteClusterOrOrphan = (idx, mainKeyword) => {
    if (idx !== undefined) {
      deleteCluster(idx);
    } else {
      setDeletedOrphans(prev => new Set([...prev, mainKeyword]));
      markDirty();
    }
  };

  const restoreDeletedKeyword = (keyword) => {
    setDeletedOrphans(prev => {
      const newSet = new Set(prev);
      newSet.delete(keyword);
      return newSet;
    });
    markDirty();
  };

  const addKeywordToCluster = (clusterIndex, keyword) => {
    const newClusters = [...manualClusters];
    const cluster = newClusters[clusterIndex];
    
    if (!cluster) return;
    
    const keywordData = keywords.find(k => k.keyword === keyword);
    if (!keywordData) return;

    const similarity = similarities.find(sim =>
      (sim.keyword1 === cluster.mainKeyword && sim.keyword2 === keyword) ||
      (sim.keyword2 === cluster.mainKeyword && sim.keyword1 === keyword)
    );

    const newKeyword = {
      keyword: keywordData.keyword,
      volume: keywordData.volume || 0,
      position: keywordData.position,
      estimatedTraffic: keywordData.estimatedTraffic || 0,
      url: keywordData.url,
      similarityScore: similarity ? Math.round(similarity.similarityScore) : 0
    };

    cluster.keywords = [...cluster.keywords, newKeyword];
    recalculateClusterMetrics(cluster);

    setManualClusters(newClusters);
    
    if (deletedOrphans.has(keyword)) {
      setDeletedOrphans(prev => {
        const newSet = new Set(prev);
        newSet.delete(keyword);
        return newSet;
      });
    }
    
    markDirty();
  };

  const setClusterCustomThreshold = (clusterIndex, threshold) => {
    const newClusters = [...manualClusters];
    const cluster = newClusters[clusterIndex];
    if (!cluster) return;

    const effectiveThreshold = threshold ?? similarityThreshold;
    const originalMainKeyword = cluster.mainKeyword;

    const secondaryKeywordNames = cluster.keywords.map(kw => kw.keyword);
    const memberKeywords = [originalMainKeyword, ...secondaryKeywordNames];

    const { clusters: subClusters } = reclusterKeywords(memberKeywords, similarities, effectiveThreshold, keywords);

    const subClustersWithThreshold = subClusters.map(subCluster => {
      const subClusterSecondaryNames = subCluster.keywords.map(kw => kw.keyword);
      const subClusterKeywordNames = [subCluster.mainKeyword, ...subClusterSecondaryNames];
      const containsOriginalMain = subClusterKeywordNames.includes(originalMainKeyword);
      return {
        ...subCluster,
        customThreshold: containsOriginalMain ? threshold : null
      };
    });

    newClusters.splice(clusterIndex, 1, ...subClustersWithThreshold);

    setManualClusters(newClusters);
    markDirty();
  };

  const removeKeywordFromCluster = (clusterIndex, keywordIndex) => {
    const newClusters = [...manualClusters];
    const cluster = newClusters[clusterIndex];
    
    if (!cluster) return;
    
    cluster.keywords = cluster.keywords.filter((_, idx) => idx !== keywordIndex);
    recalculateClusterMetrics(cluster);

    setManualClusters(newClusters);
    markDirty();
  };

  const swapWithMainKeyword = (clusterIndex, keywordIndex) => {
    const newClusters = [...manualClusters];
    const cluster = newClusters[clusterIndex];
    
    if (!cluster || !cluster.keywords[keywordIndex]) return;
    
    const keywordToPromote = cluster.keywords[keywordIndex];
    
    const oldMainKeyword = {
      keyword: cluster.mainKeyword,
      volume: cluster.mainVolume,
      position: cluster.mainPosition,
      estimatedTraffic: cluster.mainTraffic,
      url: cluster.mainUrl,
      similarityScore: keywordToPromote.similarityScore
    };
    
    cluster.mainKeyword = keywordToPromote.keyword;
    cluster.mainVolume = keywordToPromote.volume;
    cluster.mainPosition = keywordToPromote.position;
    cluster.mainTraffic = keywordToPromote.estimatedTraffic;
    cluster.mainUrl = keywordToPromote.url;
    
    cluster.keywords[keywordIndex] = oldMainKeyword;
    
    setManualClusters(newClusters);
    
    setExpandedClusters(prev => {
      const newSet = new Set(prev);
      if (prev.has(oldMainKeyword.keyword)) {
        newSet.delete(oldMainKeyword.keyword);
        newSet.add(keywordToPromote.keyword);
      }
      return newSet;
    });
    markDirty();
  };

  const moveKeywordToNewCluster = (fromClusterMainKeyword, keyword) => {
    const fromClusterIndex = manualClusters.findIndex(c => c.mainKeyword === fromClusterMainKeyword);
    if (fromClusterIndex === -1) {
      setMoveKeywordModal(null);
      return;
    }

    const fromCluster = manualClusters[fromClusterIndex];
    const keywordIndex = fromCluster.keywords.findIndex(k => k.keyword === keyword);
    if (keywordIndex === -1) {
      setMoveKeywordModal(null);
      return;
    }

    const keywordToMove = fromCluster.keywords[keywordIndex];

    const newCluster = {
      mainKeyword: keywordToMove.keyword,
      mainVolume: keywordToMove.volume,
      mainPosition: keywordToMove.position,
      mainUrl: keywordToMove.url,
      mainTraffic: keywordToMove.estimatedTraffic || 0,
      keywords: [],
      totalVolume: keywordToMove.volume,
      totalTraffic: keywordToMove.estimatedTraffic || 0,
      keywordCount: 1,
      averageSimilarity: 0,
      isNew: true
    };

    const newClusters = [...manualClusters];
    const updatedFromCluster = newClusters[fromClusterIndex];
    updatedFromCluster.keywords = updatedFromCluster.keywords.filter((_, idx) => idx !== keywordIndex);
    recalculateClusterMetrics(updatedFromCluster);

    setManualClusters([newCluster, ...newClusters]);
    setExpandedClusters(prev => new Set([...prev, keywordToMove.keyword]));
    setMoveKeywordModal(null);
    markDirty();
  };

  const moveKeywordToCluster = (fromClusterMainKeyword, keywordToMove, toClusterMainKeyword) => {
    const newClusters = [...manualClusters];
    const fromClusterIndex = newClusters.findIndex(c => c.mainKeyword === fromClusterMainKeyword);
    const toClusterIndex = newClusters.findIndex(c => c.mainKeyword === toClusterMainKeyword);
    
    if (fromClusterIndex === -1 || toClusterIndex === -1) {
      setMoveKeywordModal(null);
      return;
    }
    
    const fromCluster = newClusters[fromClusterIndex];
    const toCluster = newClusters[toClusterIndex];
    
    const keywordIndex = fromCluster.keywords.findIndex(k => k.keyword === keywordToMove);
    if (keywordIndex === -1) {
      setMoveKeywordModal(null);
      return;
    }
    
    const keywordData = fromCluster.keywords[keywordIndex];
    
    const similarity = similarities.find(sim =>
      (sim.keyword1 === toCluster.mainKeyword && sim.keyword2 === keywordData.keyword) ||
      (sim.keyword2 === toCluster.mainKeyword && sim.keyword1 === keywordData.keyword)
    );
    
    const movedKeyword = {
      ...keywordData,
      similarityScore: similarity ? Math.round(similarity.similarityScore) : 0
    };
    
    fromCluster.keywords = fromCluster.keywords.filter((_, idx) => idx !== keywordIndex);
    recalculateClusterMetrics(fromCluster);
    
    toCluster.keywords = [...toCluster.keywords, movedKeyword];
    recalculateClusterMetrics(toCluster);
    
    setManualClusters(newClusters);
    setMoveKeywordModal(null);
    markDirty();
  };

  const mergeClusters = (targetClusterMainKeyword, sourceClustersMainKeywords) => {
    let newClusters = [...manualClusters];
    
    const targetIsOrphan = orphansAsClusters.some(o => o.mainKeyword === targetClusterMainKeyword);
    if (targetIsOrphan) {
      const orphanData = orphans.find(o => o.keyword === targetClusterMainKeyword);
      if (orphanData) {
        const newCluster = {
          id: `cluster-${targetClusterMainKeyword}`,
          mainKeyword: targetClusterMainKeyword,
          mainVolume: orphanData.volume,
          mainPosition: orphanData.position,
          mainUrl: orphanData.url,
          mainTraffic: orphanData.estimatedTraffic || 0,
          keywords: [],
          totalVolume: orphanData.volume,
          totalTraffic: orphanData.estimatedTraffic || 0,
          keywordCount: 1,
          averageSimilarity: 100,
          customThreshold: null
        };
        newClusters = [...newClusters, newCluster];
      }
    }
    
    const targetIndex = newClusters.findIndex(c => c.mainKeyword === targetClusterMainKeyword);
    
    if (targetIndex === -1) {
      setMergeClusterModal(null);
      setSelectedMergeClusters(new Set());
      return;
    }
    
    const targetCluster = newClusters[targetIndex];
    const indicesToRemove = [];
    
    for (const sourceMainKeyword of sourceClustersMainKeywords) {
      let sourceIndex = newClusters.findIndex(c => c.mainKeyword === sourceMainKeyword);
      
      const sourceIsOrphan = sourceIndex === -1 && orphansAsClusters.some(o => o.mainKeyword === sourceMainKeyword);
      if (sourceIsOrphan) {
        const orphanData = orphans.find(o => o.keyword === sourceMainKeyword);
        if (orphanData) {
          const sourceSimilarity = similarities.find(sim =>
            (sim.keyword1 === targetCluster.mainKeyword && sim.keyword2 === sourceMainKeyword) ||
            (sim.keyword2 === targetCluster.mainKeyword && sim.keyword1 === sourceMainKeyword)
          );
          
          const orphanAsKeyword = {
            keyword: sourceMainKeyword,
            volume: orphanData.volume || 0,
            position: orphanData.position,
            estimatedTraffic: orphanData.estimatedTraffic || 0,
            url: orphanData.url,
            similarityScore: sourceSimilarity ? Math.round(sourceSimilarity.similarityScore) : 0
          };
          
          targetCluster.keywords = [...targetCluster.keywords, orphanAsKeyword];
        }
        continue;
      }
      
      if (sourceIndex === -1) continue;
      
      const sourceCluster = newClusters[sourceIndex];
      
      const sourceMainKeywordData = keywords.find(k => k.keyword === sourceCluster.mainKeyword);
      const sourceSimilarity = similarities.find(sim =>
        (sim.keyword1 === targetCluster.mainKeyword && sim.keyword2 === sourceCluster.mainKeyword) ||
        (sim.keyword2 === targetCluster.mainKeyword && sim.keyword1 === sourceCluster.mainKeyword)
      );
      
      const sourceMainAsKeyword = {
        keyword: sourceCluster.mainKeyword,
        volume: sourceMainKeywordData?.volume || sourceCluster.mainVolume || 0,
        position: sourceMainKeywordData?.position || sourceCluster.mainPosition,
        estimatedTraffic: sourceMainKeywordData?.estimatedTraffic || sourceCluster.mainTraffic || 0,
        url: sourceMainKeywordData?.url || sourceCluster.mainUrl,
        similarityScore: sourceSimilarity ? Math.round(sourceSimilarity.similarityScore) : 0
      };
      
      const sourceKeywordsWithSimilarity = sourceCluster.keywords.map(kw => {
        const similarity = similarities.find(sim =>
          (sim.keyword1 === targetCluster.mainKeyword && sim.keyword2 === kw.keyword) ||
          (sim.keyword2 === targetCluster.mainKeyword && sim.keyword1 === kw.keyword)
        );
        return {
          ...kw,
          similarityScore: similarity ? Math.round(similarity.similarityScore) : 0
        };
      });
      
      targetCluster.keywords = [...targetCluster.keywords, sourceMainAsKeyword, ...sourceKeywordsWithSimilarity];
      indicesToRemove.push(sourceIndex);
    }
    
    recalculateClusterMetrics(targetCluster);
    
    indicesToRemove.sort((a, b) => b - a).forEach(idx => {
      newClusters.splice(idx, 1);
    });
    
    setManualClusters(newClusters);
    setIsManualMode(true);
    setMergeClusterModal(null);
    setSelectedMergeClusters(new Set());
    markDirty();
  };

  const toggleCluster = (mainKeyword) => {
    setExpandedClusters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(mainKeyword)) {
        newSet.delete(mainKeyword);
      } else {
        newSet.add(mainKeyword);
      }
      return newSet;
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [excludedKeywords1, excludedKeywords2, sortConfig]);

  if (loading) {
    return (
      <div className="similarities-loading">
        <LoadingSpinner size="lg" />
        <p>Calcul des similarités...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="similarities-error">
        <Icon name="AlertCircle" size={48} color="var(--color-error)" />
        <p>Erreur lors du chargement des similarités</p>
        <small>{error}</small>
      </div>
    );
  }

  if (similarities.length === 0) {
    return (
      <div className="similarities-empty">
        <Icon name="Search" size={48} color="var(--color-gray-400)" />
        <p>Aucune similitude trouvée</p>
        <small>Les mots-clés analysés n'ont pas de SERP similaires</small>
      </div>
    );
  }

  return (
    <div className="similarities-container">
      {(viewChangePending || autoSaveInProgress) && activeView === "clustering" && (
        <div className="save-overlay">
          <div className="save-overlay-content">
            <div className="save-spinner"></div>
            <span>Sauvegarde en cours...</span>
          </div>
        </div>
      )}
      <div className="similarities-header">
        <div className="similarities-tabs">
          <button
            className={`tab ${activeView === "keywords" ? "tab-active" : ""}`}
            onClick={() => handleViewChange("keywords")}
            disabled={viewChangePending}
          >
            Mots-clés
          </button>
          <button
            className={`tab ${activeView === "serps" ? "tab-active" : ""}`}
            onClick={() => handleViewChange("serps")}
            disabled={viewChangePending}
          >
            Serps
          </button>
          <button
            className={`tab ${activeView === "clustering" ? "tab-active" : ""}`}
            onClick={() => handleViewChange("clustering")}
            disabled={viewChangePending}
          >
            Clusterisation
          </button>
          <button
            className={`tab ${activeView === "optimization" ? "tab-active" : ""}`}
            onClick={() => handleViewChange("optimization")}
            disabled={viewChangePending}
          >
            Optimisation
          </button>
        </div>
        <div className="similarities-header-actions">
          {activeView === "clustering" && !savingClusters && (
            <button 
              className="save-clusters-btn"
              onClick={triggerSave}
              title="Sauvegarder les clusters"
              style={{ 
                padding: '6px 12px', 
                fontSize: '13px', 
                background: 'var(--color-primary, #3b82f6)', 
                color: 'white', 
                border: 'none', 
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginRight: '12px'
              }}
            >
              <Icon name="Save" size={14} />
              Sauvegarder
            </button>
          )}
          {savingClusters && (
            <span className="saving-indicator" style={{ marginRight: '12px', fontSize: '13px', color: 'var(--color-primary)' }}>
              <Icon name="Loader" size={14} className="spinning" style={{ display: 'inline', marginRight: '4px' }} />
              Sauvegarde...
            </span>
          )}
          {saveSuccess && (
            <div className="save-toast">
              <Icon name="CheckCircle" size={18} />
              <span>Clusters sauvegardés avec succès</span>
            </div>
          )}
          <div className="gads-filter-container" ref={filterDropdownRef}>
            <button 
              className="gads-add-filter-btn"
              onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
            >
              <Icon name="Filter" size={16} />
              Ajouter un filtre
            </button>

            {filterDropdownOpen && (
              <div className="gads-filter-dropdown">
                <div className="gads-dropdown-search">
                  <Icon name="Search" size={14} />
                  <input
                    type="text"
                    placeholder="Rechercher"
                    value={filterDropdownSearch}
                    onChange={(e) => setFilterDropdownSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="gads-dropdown-categories">
                  {Object.entries(filterMenuConfig).map(([catKey, cat]) => {
                    const filteredFields = cat.fields.filter(f =>
                      !filterDropdownSearch || f.label.toLowerCase().includes(filterDropdownSearch.toLowerCase())
                    );
                    if (filterDropdownSearch && filteredFields.length === 0) return null;
                    
                    return (
                      <div key={catKey} className="gads-dropdown-category">
                        <button
                          className={`gads-category-header ${expandedFilterCategories.has(catKey) ? 'expanded' : ''}`}
                          onClick={() => toggleFilterCategory(catKey)}
                        >
                          <span>{cat.label}</span>
                          <Icon name={expandedFilterCategories.has(catKey) ? 'ChevronUp' : 'ChevronDown'} size={16} />
                        </button>
                        {expandedFilterCategories.has(catKey) && (
                          <div className="gads-category-items">
                            {filteredFields.map(f => (
                              <button
                                key={f.field}
                                className="gads-filter-option"
                                onClick={() => openFilterConfig(catKey, f.field, f.label)}
                              >
                                {f.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="threshold-control-inline">
            <label htmlFor="similarity-threshold">
              Seuil de similarité : <strong>{similarityThreshold}%</strong>
              {activeView === "optimization" && (
                <Icon name="Lock" size={13} style={{ marginLeft: '6px', color: 'var(--color-gray-500)', verticalAlign: 'text-top', position: 'relative', top: '3px' }} />
              )}
            </label>
            <input
              id="similarity-threshold"
              type="range"
              min="0"
              max="100"
              step="5"
              value={similarityThreshold}
              disabled={activeView === "optimization"}
              onChange={(e) => {
                const newThreshold = Number(e.target.value);
                setSimilarityThreshold(newThreshold);
                if (hasLoadedSavedClusters.current) {
                  hasLoadedSavedClusters.current = false;
                }
                markDirty();
              }}
              className="threshold-slider"
            />
          </div>
        </div>
      </div>

      {(activeFilters.length > 0 || regexFilters.regexInclude || regexFilters.regexExclude) && (
        <div className="gads-active-filters">
          {activeFilters.map(filter => {
            const catConfig = filter.category === 'cluster' ? filterTypeConfig.cluster : filterTypeConfig.keyword;
            const label = catConfig[filter.field]?.label || filter.field;
            const opDisplay = filter.operator === 'between' 
              ? `${filter.value} - ${filter.value2}` 
              : `${filter.operator} ${filter.value}`;
            return (
              <span 
                key={filter.id} 
                className="gads-filter-tag gads-filter-tag-clickable"
                onClick={() => editExistingFilter(filter)}
              >
                <span className="gads-tag-label">{label}</span>
                <span className="gads-tag-value">{opDisplay}</span>
                <button 
                  className="gads-tag-remove" 
                  onClick={(e) => { e.stopPropagation(); removeFilter(filter.id); }}
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            );
          })}
          {regexFilters.regexInclude && (
            <span 
              className="gads-filter-tag gads-filter-tag-clickable"
              onClick={() => editRegexFilter('regexInclude')}
            >
              <span className="gads-tag-label">Inclure</span>
              <span className="gads-tag-value">{regexFilters.regexInclude}</span>
              <button 
                className="gads-tag-remove" 
                onClick={(e) => { e.stopPropagation(); removeRegexFilter('regexInclude'); }}
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}
          {regexFilters.regexExclude && (
            <span 
              className="gads-filter-tag gads-filter-tag-clickable"
              onClick={() => editRegexFilter('regexExclude')}
            >
              <span className="gads-tag-label">Exclure</span>
              <span className="gads-tag-value">{regexFilters.regexExclude}</span>
              <button 
                className="gads-tag-remove" 
                onClick={(e) => { e.stopPropagation(); removeRegexFilter('regexExclude'); }}
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}
          <span className="gads-filters-count">
            {filteredClustersForDisplay.length} / {allClustersForDisplay.length} clusters
          </span>
          <button className="gads-clear-all" onClick={resetAllFilters}>
            Tout effacer
          </button>
        </div>
      )}

      {filterConfigModal && (
        <div className="gads-modal-overlay" onClick={() => setFilterConfigModal(null)}>
          <div className="gads-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gads-modal-header">
              <span>{filterConfigModal.label}</span>
              <button className="gads-modal-close" onClick={() => setFilterConfigModal(null)}>
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="gads-modal-body">
              {filterConfigModal.category === 'regex' ? (
                <input
                  type="text"
                  className="gads-modal-input gads-modal-input-regex"
                  placeholder="Ex: pizza|burger"
                  value={pendingFilterConfig.value}
                  onChange={(e) => setPendingFilterConfig(prev => ({ ...prev, value: e.target.value }))}
                  autoFocus
                />
              ) : (
                <div className="gads-modal-row">
                  <select
                    className="gads-modal-operator"
                    value={pendingFilterConfig.operator}
                    onChange={(e) => setPendingFilterConfig(prev => ({ ...prev, operator: e.target.value }))}
                  >
                    <option value=">">&gt;</option>
                    <option value="<">&lt;</option>
                    <option value="=">=</option>
                    <option value=">=">&ge;</option>
                    <option value="<=">&le;</option>
                    <option value="between">Entre</option>
                  </select>
                  <input
                    type="number"
                    className="gads-modal-input"
                    placeholder="Valeur"
                    value={pendingFilterConfig.value}
                    onChange={(e) => setPendingFilterConfig(prev => ({ ...prev, value: e.target.value }))}
                    autoFocus
                  />
                  {pendingFilterConfig.operator === 'between' && (
                    <>
                      <span className="gads-modal-sep">et</span>
                      <input
                        type="number"
                        className="gads-modal-input"
                        placeholder="Max"
                        value={pendingFilterConfig.value2}
                        onChange={(e) => setPendingFilterConfig(prev => ({ ...prev, value2: e.target.value }))}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="gads-modal-footer">
              <button 
                className="gads-modal-apply"
                onClick={applyFilterConfig}
                disabled={!pendingFilterConfig.value}
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}

      {activeView === "keywords" && (
        <>
          <div className="similarities-info">
            <p className="info-text">
              {sortedSimilarities.length} / {similarities.length} paires de mots-clés similaires
            </p>
            <div className="similarities-actions">
              {(excludedKeywords1.length > 0 || excludedKeywords2.length > 0) && (
                <button 
                  className="clear-filters-btn"
                  onClick={() => { setExcludedKeywords1([]); setExcludedKeywords2([]); }}
                >
                  Effacer les filtres
                </button>
              )}
              {sortConfig.length > 0 && (
                <button
                  className="clear-sort-btn"
                  onClick={() => setSortConfig([{ key: "similarityScore", direction: "desc" }])}
                >
                  Réinitialiser le tri
                </button>
              )}
              <button
                className="export-btn"
                onClick={handleExportSimilarities}
                disabled={exportablePairs.length === 0}
              >
                <Icon name="Download" size={16} />
                Exporter en Excel
              </button>
            </div>
          </div>

          <div className="similarities-table-wrapper">
            <table className="similarities-table">
              <thead>
                <tr>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort("keyword1")}
                  >
                    Mot-clé 1 {getSortIndicator("keyword1")}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort("volume1")}
                  >
                    Volume {getSortIndicator("volume1")}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort("keyword2")}
                  >
                    Mot-clé 2 {getSortIndicator("keyword2")}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort("volume2")}
                  >
                    Volume {getSortIndicator("volume2")}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort("similarityScore")}
                  >
                    Similarité {getSortIndicator("similarityScore")}
                  </th>
                </tr>
                <tr className="filter-row">
                  <th>
                    <div className="dropdown-container-inline">
                      <input
                        type="text"
                        className="dropdown-search"
                        placeholder="Filtrer..."
                        value={searchTerm1}
                        onChange={(e) => setSearchTerm1(e.target.value)}
                        onFocus={() => setIsDropdown1Open(true)}
                      />
                      {isDropdown1Open && (
                        <>
                          <div 
                            className="dropdown-backdrop" 
                            onClick={() => setIsDropdown1Open(false)} 
                          />
                          <div className="dropdown-menu-inline">
                            <div className="dropdown-actions">
                              <button onClick={includeAll1}>Tout cocher</button>
                              <button onClick={excludeAll1}>Tout décocher</button>
                            </div>
                            {filteredDropdownKeywords1.map((keyword) => (
                              <label key={keyword} className="dropdown-item">
                                <input
                                  type="checkbox"
                                  checked={!excludedKeywords1.includes(keyword)}
                                  onChange={() => toggleKeyword1(keyword)}
                                />
                                <span>{keyword}</span>
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                      <span className="filter-count">{allKeywords1.length - excludedKeywords1.length}/{allKeywords1.length}</span>
                    </div>
                  </th>
                  <th></th>
                  <th>
                    <div className="dropdown-container-inline">
                      <input
                        type="text"
                        className="dropdown-search"
                        placeholder="Filtrer..."
                        value={searchTerm2}
                        onChange={(e) => setSearchTerm2(e.target.value)}
                        onFocus={() => setIsDropdown2Open(true)}
                      />
                      {isDropdown2Open && (
                        <>
                          <div 
                            className="dropdown-backdrop" 
                            onClick={() => setIsDropdown2Open(false)} 
                          />
                          <div className="dropdown-menu-inline">
                            <div className="dropdown-actions">
                              <button onClick={includeAll2}>Tout cocher</button>
                              <button onClick={excludeAll2}>Tout décocher</button>
                            </div>
                            {filteredDropdownKeywords2.map((keyword) => (
                              <label key={keyword} className="dropdown-item">
                                <input
                                  type="checkbox"
                                  checked={!excludedKeywords2.includes(keyword)}
                                  onChange={() => toggleKeyword2(keyword)}
                                />
                                <span>{keyword}</span>
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                      <span className="filter-count">{allKeywords2.length - excludedKeywords2.length}/{allKeywords2.length}</span>
                    </div>
                  </th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
          <tbody>
            {paginatedSimilarities.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-row">
                  Aucun résultat avec les filtres actuels
                </td>
              </tr>
            ) : (
              paginatedSimilarities.map((sim) => (
                <tr key={`${sim.keyword1}-${sim.keyword2}`}>
                  <td className="keyword-cell">{sim.keyword1}</td>
                  <td className="volume-cell">{formatNumber(sim.volume1)}</td>
                  <td className="keyword-cell">{sim.keyword2}</td>
                  <td className="volume-cell">{formatNumber(sim.volume2)}</td>
                  <td className="similarity-cell">
                    <span className={`similarity-badge ${sim.similarityScore >= similarityThreshold ? 'above-threshold' : 'below-threshold'}`}>
                      {Math.round(sim.similarityScore)} %
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1}
          >
            «
          </button>
          <button 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
            disabled={currentPage === 1}
          >
            ‹
          </button>
          <span className="pagination-info">
            Page {currentPage} / {totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
            disabled={currentPage === totalPages}
          >
            ›
          </button>
          <button 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages}
          >
            »
          </button>
        </div>
      )}
        </>
      )}

      {activeView === "serps" && (
        <SerpComparison studyId={studyId} similarities={similarities} keywords={keywords} />
      )}

      {activeView === "clustering" && (
        <>
          <div className="similarities-info">
            <div className="info-left">
              <p className="info-text">
                {allClustersForDisplay.length} clusters • {allClustersForDisplay.reduce((sum, c) => sum + c.keywordCount, 0)} mots-clés
              </p>
              <div className="clustering-info-tooltip">
                <Icon name="Info" size={16} />
                <div className="tooltip-content">
                  <h4>Comment fonctionne l'algorithme de clustering ?</h4>
                  <p><strong>1. Calcul de similarité SERP</strong></p>
                  <p>Compare les résultats Google (URLs, positions) entre chaque paire de mots-clés. Plus les résultats sont identiques, plus la similarité est élevée.</p>
                  
                  <p><strong>2. Filtrage par seuil</strong></p>
                  <p>Seules les paires dépassant le seuil défini sont utilisées pour créer les clusters.</p>
                  
                  <p><strong>3. Regroupement par transitivité</strong></p>
                  <p>Algorithme Union-Find : regroupe automatiquement les mots-clés similaires :</p>
                  <ul>
                    <li>Si "pizza" et "pizzeria" ont des SERPs similaires (85%)</li>
                    <li>Et "pizzeria" et "restaurant italien" aussi (75%)</li>
                    <li>Alors les 3 mots-clés sont regroupés dans le même cluster</li>
                  </ul>
                  
                  <p><strong>4. Mot-clé principal</strong></p>
                  <p>Sélectionné automatiquement : celui avec le plus gros volume de recherche du cluster.</p>
                  
                  <p className="tooltip-note">💡 <strong>Astuce :</strong> Baissez le seuil pour obtenir de plus gros clusters. Augmentez-le pour des clusters plus précis basés sur une intention de recherche très similaire.</p>
                </div>
              </div>
            </div>
            <div className="info-actions">
              <div className="cluster-list-search">
                <Icon name="Search" size={16} className="search-icon" />
                <input
                  type="text"
                  className="cluster-list-search-input"
                  placeholder="Rechercher..."
                  value={clusterListSearchTerm}
                  onChange={(e) => { setClusterListSearchTerm(e.target.value); setClusterPage(1); }}
                />
                {clusterListSearchTerm && (
                  <button className="clear-search" onClick={() => { setClusterListSearchTerm(''); setClusterPage(1); }}>
                    <Icon name="X" size={14} />
                  </button>
                )}
              </div>
              <button className="create-cluster-btn" onClick={() => setIsCreatingCluster(true)}>
                <Icon name="Plus" size={16} />
                Nouveau cluster
              </button>
              {deletedOrphans.size > 0 && (
                <button 
                  className={`btn-show-deleted ${showDeletedKeywords ? 'active' : ''}`}
                  onClick={() => setShowDeletedKeywords(!showDeletedKeywords)}
                >
                  <Icon name="Eye" size={16} />
                  Non clusterisés ({deletedOrphans.size})
                </button>
              )}
            </div>
          </div>

          {showDeletedKeywords && deletedOrphans.size > 0 && (
            <div className="deleted-keywords-panel">
              <div className="deleted-keywords-header">
                <h4>Mots-clés non clusterisés ({deletedOrphans.size})</h4>
                <button 
                  className="btn-close-panel"
                  onClick={() => setShowDeletedKeywords(false)}
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
              <div className="deleted-keywords-list">
                {[...deletedOrphans].map(keyword => {
                  const kwData = keywords.find(k => k.keyword === keyword);
                  return (
                    <div key={keyword} className="deleted-keyword-item">
                      <span className="deleted-keyword-name">{keyword}</span>
                      <span className="deleted-keyword-volume">{kwData ? formatNumber(kwData.volume) : '-'}</span>
                      <button 
                        className="btn-restore-keyword"
                        onClick={() => restoreDeletedKeyword(keyword)}
                        title="Restaurer"
                      >
                        <Icon name="RotateCcw" size={14} />
                        Restaurer
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isCreatingCluster && (
            <div className="new-cluster-selector">
              <div className="new-cluster-header">
                <h3>Choisir le mot-clé principal du nouveau cluster</h3>
                <button 
                  className="btn-close-selector"
                  onClick={() => {
                    setIsCreatingCluster(false);
                    setNewClusterSearchTerm('');
                  }}
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
              <input
                type="text"
                className="new-cluster-search"
                placeholder="Rechercher un mot-clé..."
                value={newClusterSearchTerm}
                onChange={(e) => setNewClusterSearchTerm(e.target.value)}
                autoFocus
              />
              <div className="new-cluster-list">
                {keywords
                  .filter(k => 
                    !clusteredKeywords.has(k.keyword) &&
                    k.keyword.toLowerCase().includes(newClusterSearchTerm.toLowerCase())
                  )
                  .slice(0, 50)
                  .map(k => (
                    <button
                      key={k.keyword}
                      className="new-cluster-item"
                      onClick={() => createNewCluster(k.keyword)}
                    >
                      <span className="cluster-item-keyword">{k.keyword}</span>
                      <span className="cluster-item-volume">{formatNumber(k.volume)}</span>
                    </button>
                  ))
                }
              </div>
            </div>
          )}

          <div className="clusters-list">
            {allClustersForDisplay.length === 0 ? (
              <div className="similarities-empty">
                <Icon name="Search" size={48} color="var(--color-gray-400)" />
                <p>Aucun cluster trouvé</p>
                <small>Essayez de diminuer le seuil de similarité</small>
              </div>
            ) : filteredClustersForDisplay.length === 0 ? (
              <div className="similarities-empty">
                <Icon name="Search" size={48} color="var(--color-gray-400)" />
                <p>Aucun résultat pour "{clusterListSearchTerm}"</p>
                <small>Essayez un autre terme de recherche</small>
              </div>
            ) : (
              <>
                {filteredClustersForDisplay.slice((clusterPage - 1) * clustersPerPage, clusterPage * clustersPerPage).map((cluster) => {
                  const idx = clusterIndexMap.get(cluster.mainKeyword);
                  const isSingleKeywordCluster = cluster.keywordCount === 1;
                  return (
                    <div key={cluster.mainKeyword} className="cluster-card">
                      <div className="cluster-header-row">
                        <div className="cluster-main-row">
                          <div className="cluster-keyword-cell">
                            <strong className="cluster-name" onClick={() => toggleCluster(cluster.mainKeyword)}>
                              {cluster.mainKeyword}
                            </strong>
                        <span className="cluster-count">({cluster.keywordCount} mot{cluster.keywordCount > 1 ? 's' : ''}-clé{cluster.keywordCount > 1 ? 's' : ''})</span>
                      </div>
                      <div className="cluster-metric cluster-volume">{formatNumber(cluster.mainVolume)}</div>
                      <div className="cluster-metric cluster-position" style={{ 
                        color: cluster.mainPosition && cluster.mainPosition <= 100 
                          ? (cluster.mainPosition <= 3 ? 'var(--color-success)' 
                            : cluster.mainPosition <= 10 ? 'var(--color-primary)' 
                            : cluster.mainPosition <= 20 ? 'var(--color-warning)' 
                            : 'var(--color-gray-600)')
                          : 'var(--color-gray-400)',
                        fontWeight: 600
                      }}>
                        {cluster.mainPosition && cluster.mainPosition <= 100 ? `#${cluster.mainPosition}` : '-'}
                      </div>
                      <div className="cluster-metric cluster-traffic">{formatNumber(cluster.mainTraffic || 0)}</div>
                      <div className="cluster-metric cluster-similarity"></div>
                      <div className="cluster-url-cell" title={cluster.mainUrl}>
                        {cluster.mainUrl ? (
                          <a href={cluster.mainUrl} target="_blank" rel="noopener noreferrer">
                            {cleanUrl(cluster.mainUrl)}
                          </a>
                        ) : (
                          cleanUrl(cluster.mainUrl)
                        )}
                      </div>
                    </div>
                    
                    <div className="cluster-actions">
                      <div className={`cluster-threshold-control ${cluster.customThreshold != null ? 'has-custom' : ''}`} title="Seuil de similarité pour ce cluster">
                        <span className="threshold-label">
                          {cluster.customThreshold != null ? `${cluster.customThreshold}%` : `Global`}
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          value={cluster.customThreshold ?? similarityThreshold}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            const val = parseInt(e.target.value, 10);
                            if (idx !== undefined) {
                              setClusterCustomThreshold(idx, val);
                            } else {
                              const newIdx = convertOrphanToCluster(cluster.mainKeyword);
                              if (newIdx >= 0) {
                                setTimeout(() => setClusterCustomThreshold(newIdx, val), 0);
                              }
                            }
                          }}
                          className="cluster-threshold-slider"
                        />
                        {cluster.customThreshold != null && (
                          <button
                            className="btn-reset-threshold"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (idx !== undefined) {
                                setClusterCustomThreshold(idx, null);
                              }
                            }}
                            title="Réinitialiser au seuil global"
                          >
                            <Icon name="RotateCcw" size={12} />
                          </button>
                        )}
                      </div>
                      {isSingleKeywordCluster ? (
                        <>
                          <button 
                            className="btn-icon-similarity btn-merge" 
                            onClick={(e) => { e.stopPropagation(); setMergeClusterModal({ clusterMainKeyword: cluster.mainKeyword }); setMergeClusterSearchTerm(''); setSelectedMergeClusters(new Set()); }}
                            title="Fusionner avec un autre cluster"
                          >
                            <Icon name="Merge" size={16} />
                          </button>
                          <button 
                            className="btn-icon-similarity btn-danger" 
                            onClick={(e) => { e.stopPropagation(); if(window.confirm('Retirer ce mot-clé de la clusterisation ?')) deleteClusterOrOrphan(idx, cluster.mainKeyword); }}
                            title="Exclure"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="btn-icon-similarity btn-merge" 
                            onClick={(e) => { e.stopPropagation(); setMergeClusterModal({ clusterMainKeyword: cluster.mainKeyword }); setMergeClusterSearchTerm(''); setSelectedMergeClusters(new Set()); }}
                            title="Fusionner avec un autre cluster"
                          >
                            <Icon name="Merge" size={16} />
                          </button>
                          <button 
                            className="btn-icon-similarity btn-danger" 
                            onClick={(e) => { e.stopPropagation(); if(window.confirm('Supprimer ce cluster ?')) deleteClusterOrOrphan(idx, cluster.mainKeyword); }}
                            title="Supprimer"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="cluster-table-header">
                    <div className="cluster-table-header-row">
                      <div 
                        className="header-cell keyword-header keyword-header-clickable" 
                        onClick={() => toggleCluster(cluster.mainKeyword)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleCluster(cluster.mainKeyword)}
                        aria-expanded={expandedClusters.has(cluster.mainKeyword)}
                      >
                        <Icon name={expandedClusters.has(cluster.mainKeyword) ? "ChevronDown" : "ChevronRight"} size={14} />
                        Mot-clé
                      </div>
                      <div className="header-cell volume-header">Volume</div>
                      <div className="header-cell position-header">Position</div>
                      <div className="header-cell traffic-header">Trafic estimé</div>
                      <div className="header-cell similarity-header">Similarité</div>
                      <div className="header-cell url-header">URL</div>
                      <div className="header-cell actions-header">Actions</div>
                    </div>
                  </div>
                  
                  {expandedClusters.has(cluster.mainKeyword) && (
                    <>
                      <div className="cluster-keywords">
                        <table className="keywords-table">
                          <tbody>
                            {cluster.keywords.length === 0 ? (
                              <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '16px', color: 'var(--color-gray-500)', fontStyle: 'italic' }}>
                                  Aucun mot-clé secondaire
                                </td>
                              </tr>
                            ) : cluster.keywords.map((kw, kwIdx) => (
                                  <tr key={kw.keyword}>
                                    <td>{kw.keyword}</td>
                                    <td>{formatNumber(kw.volume)}</td>
                                    <td style={{ 
                                      color: kw.position && kw.position <= 100 
                                        ? (kw.position <= 3 ? 'var(--color-success)' 
                                          : kw.position <= 10 ? 'var(--color-primary)' 
                                          : kw.position <= 20 ? 'var(--color-warning)' 
                                          : 'var(--color-gray-600)')
                                        : 'var(--color-gray-400)',
                                      fontWeight: 600
                                    }}>
                                      {kw.position && kw.position <= 100 ? `#${kw.position}` : '-'}
                                    </td>
                                    <td>{formatNumber(kw.estimatedTraffic || 0)}</td>
                                    <td>
                                      <span className="similarity-badge">
                                        {kw.similarityScore}%
                                  </span>
                                </td>
                                <td className="url-cell" title={kw.url}>
                                  {kw.url ? (
                                    <a href={kw.url} target="_blank" rel="noopener noreferrer">
                                      {cleanUrl(kw.url)}
                                    </a>
                                  ) : (
                                    cleanUrl(kw.url)
                                  )}
                                </td>
                                <td className="actions-cell">
                                  <div className="keyword-actions">
                                    <button 
                                      className="btn-icon-action"
                                      onClick={() => swapWithMainKeyword(idx, kwIdx)}
                                      title="Définir comme mot-clé principal"
                                    >
                                      <Icon name="ArrowUpCircle" size={14} />
                                    </button>
                                    <button 
                                      className="btn-icon-action"
                                      onClick={() => setMoveKeywordModal({ 
                                        fromClusterMainKeyword: cluster.mainKeyword, 
                                        keyword: kw.keyword 
                                      })}
                                      title="Déplacer vers un autre cluster"
                                    >
                                      <Icon name="Move" size={14} />
                                    </button>
                                    <button 
                                      className="btn-remove-keyword"
                                      onClick={() => removeKeywordFromCluster(idx, kwIdx)}
                                      title="Retirer du cluster"
                                    >
                                      <Icon name="X" size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                            <tr className="add-keyword-row">
                              <td colSpan={1} className="add-keyword-cell">
                                <div className="dropdown-container-inline add-keyword-dropdown">
                                  <input
                                    type="text"
                                    className="dropdown-search"
                                    placeholder="+ Ajouter un mot-clé..."
                                    value={addingToCluster === idx ? keywordSearchTerm : ''}
                                    onFocus={(e) => {
                                      const rect = e.target.getBoundingClientRect();
                                      setDropdownPosition({
                                        top: rect.bottom + 4,
                                        left: rect.left,
                                        width: rect.width
                                      });
                                      setAddingToCluster(idx);
                                      setKeywordSearchTerm('');
                                    }}
                                    onChange={(e) => setKeywordSearchTerm(e.target.value)}
                                  />
                                  {addingToCluster === idx && (
                                    <>
                                      <div 
                                        className="dropdown-backdrop" 
                                        onClick={() => {
                                          setAddingToCluster(null);
                                          setKeywordSearchTerm('');
                                        }}
                                      />
                                      <div 
                                        className="dropdown-menu-inline"
                                        style={{
                                          top: `${dropdownPosition.top}px`,
                                          left: `${dropdownPosition.left}px`,
                                          minWidth: `${Math.max(dropdownPosition.width, 400)}px`
                                        }}
                                      >
                                        {keywords
                                          .filter(k => 
                                            k.keyword !== cluster.mainKeyword && 
                                            !cluster.keywords.find(ck => ck.keyword === k.keyword) &&
                                            k.keyword.toLowerCase().includes(keywordSearchTerm.toLowerCase())
                                          )
                                          .slice(0, 50)
                                          .map(k => (
                                            <div
                                              key={k.keyword}
                                              className="dropdown-item-keyword"
                                              onClick={() => {
                                                addKeywordToCluster(idx, k.keyword);
                                                setAddingToCluster(null);
                                                setKeywordSearchTerm('');
                                              }}
                                            >
                                              <span className="keyword-text">{k.keyword}</span>
                                              <span className="keyword-volume">{formatNumber(k.volume)}</span>
                                            </div>
                                          ))
                                        }
                                      </div>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="cluster-totals">
                        <div className="total-item">
                          <span className="total-label">Volume total :</span>
                          <span className="total-value">{formatNumber(cluster.totalVolume)}</span>
                        </div>
                        <div className="total-item">
                          <span className="total-label">Trafic estimé total :</span>
                          <span className="total-value">{formatNumber(cluster.totalTraffic)}</span>
                        </div>
                        <div className="total-item">
                          <span className="total-label">Similarité moyenne :</span>
                          <span className="total-value">{cluster.averageSimilarity}%</span>
                        </div>
                      </div>
                    </>
                  )}
                    </div>
                  );
                })}
{filteredClustersForDisplay.length > clustersPerPage && (
                  <div className="clusters-pagination">
                    <button 
                      className="btn-pagination"
                      onClick={() => setClusterPage(1)}
                      disabled={clusterPage === 1}
                    >
                      <Icon name="ChevronsLeft" size={16} />
                    </button>
                    <button 
                      className="btn-pagination"
                      onClick={() => setClusterPage(p => Math.max(1, p - 1))}
                      disabled={clusterPage === 1}
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </button>
                    <span className="pagination-info">
                      Page {clusterPage} / {Math.ceil(filteredClustersForDisplay.length / clustersPerPage)} ({filteredClustersForDisplay.length} clusters)
                    </span>
                    <button 
                      className="btn-pagination"
                      onClick={() => setClusterPage(p => Math.min(Math.ceil(filteredClustersForDisplay.length / clustersPerPage), p + 1))}
                      disabled={clusterPage >= Math.ceil(filteredClustersForDisplay.length / clustersPerPage)}
                    >
                      <Icon name="ChevronRight" size={16} />
                    </button>
                    <button 
                      className="btn-pagination"
                      onClick={() => setClusterPage(Math.ceil(filteredClustersForDisplay.length / clustersPerPage))}
                      disabled={clusterPage >= Math.ceil(filteredClustersForDisplay.length / clustersPerPage)}
                    >
                      <Icon name="ChevronsRight" size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {moveKeywordModal && (
            <div 
              className="modal-overlay" 
              onClick={() => setMoveKeywordModal(null)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="move-keyword-modal-title"
            >
              <div className="modal-content modal-move-keyword" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3 id="move-keyword-modal-title">Déplacer le mot-clé</h3>
                  <button 
                    className="btn-close-modal"
                    onClick={() => setMoveKeywordModal(null)}
                    aria-label="Fermer"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
                <div className="modal-body">
                  <p className="move-keyword-info">
                    Déplacer <strong>{moveKeywordModal.keyword}</strong> vers :
                  </p>
                  <button
                    className="cluster-selection-item cluster-selection-new"
                    onClick={() => moveKeywordToNewCluster(
                      moveKeywordModal.fromClusterMainKeyword,
                      moveKeywordModal.keyword
                    )}
                  >
                    <div className="cluster-selection-main">
                      <Icon name="Plus" size={16} />
                      <strong>Nouveau cluster</strong>
                      <span className="cluster-selection-count">(créer un cluster avec ce mot-clé)</span>
                    </div>
                  </button>
                  <div className="clusters-selection-divider"></div>
                  <div className="move-cluster-search-container">
                    <input
                      type="text"
                      className="move-cluster-search"
                      placeholder="Rechercher un cluster..."
                      value={moveClusterSearchTerm}
                      onChange={(e) => setMoveClusterSearchTerm(e.target.value)}
                      autoFocus
                    />
                    {moveClusterSearchTerm && (
                      <button
                        className="clear-search-btn"
                        onClick={() => setMoveClusterSearchTerm('')}
                        aria-label="Effacer la recherche"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    )}
                  </div>
                  <div className="clusters-selection-list">
                    {(() => {
                      const filteredClusters = clusters.filter(c => 
                        c.mainKeyword !== moveKeywordModal.fromClusterMainKeyword &&
                        c.mainKeyword.toLowerCase().includes(moveClusterSearchTerm.toLowerCase())
                      );

                      if (filteredClusters.length === 0) {
                        return (
                          <div className="no-clusters-found">
                            <Icon name="Search" size={32} color="var(--color-gray-400)" />
                            <p>Aucun cluster trouvé</p>
                            {moveClusterSearchTerm && (
                              <small>Essayez un autre terme de recherche</small>
                            )}
                          </div>
                        );
                      }

                      return filteredClusters.map((targetCluster) => {
                        return (
                          <button
                            key={targetCluster.mainKeyword}
                            className="cluster-selection-item"
                            onClick={() => moveKeywordToCluster(
                              moveKeywordModal.fromClusterMainKeyword,
                              moveKeywordModal.keyword,
                              targetCluster.mainKeyword
                            )}
                          >
                            <div className="cluster-selection-main">
                              <strong>{targetCluster.mainKeyword}</strong>
                              <span className="cluster-selection-count">({targetCluster.keywordCount} mots-clés)</span>
                            </div>
                            <div className="cluster-selection-volume">
                              {formatNumber(targetCluster.totalVolume)} vol.
                            </div>
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {mergeClusterModal && (
            <div 
              className="modal-overlay" 
              onClick={() => setMergeClusterModal(null)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="merge-cluster-modal-title"
            >
              <div className="modal-content modal-merge-cluster" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3 id="merge-cluster-modal-title">Fusionner les clusters</h3>
                  <button 
                    className="btn-close-modal"
                    onClick={() => setMergeClusterModal(null)}
                    aria-label="Fermer"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
                <div className="modal-body">
                  <p className="move-keyword-info">
                    Fusionner dans <strong>{mergeClusterModal.clusterMainKeyword}</strong> :
                    {selectedMergeClusters.size > 0 && (
                      <span className="selected-count"> ({selectedMergeClusters.size} sélectionné{selectedMergeClusters.size > 1 ? 's' : ''})</span>
                    )}
                  </p>
                  <div className="move-cluster-search-container">
                    <input
                      type="text"
                      className="move-cluster-search"
                      placeholder="Rechercher un cluster..."
                      value={mergeClusterSearchTerm}
                      onChange={(e) => setMergeClusterSearchTerm(e.target.value)}
                      autoFocus
                    />
                    {mergeClusterSearchTerm && (
                      <button
                        className="clear-search-btn"
                        onClick={() => setMergeClusterSearchTerm('')}
                        aria-label="Effacer la recherche"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    )}
                  </div>
                  <div className="clusters-selection-list">
                    {(() => {
                      const filteredClusters = allClustersForDisplay.filter(c => 
                        c.mainKeyword !== mergeClusterModal.clusterMainKeyword &&
                        c.mainKeyword.toLowerCase().includes(mergeClusterSearchTerm.toLowerCase())
                      );

                      if (filteredClusters.length === 0) {
                        return (
                          <div className="no-clusters-found">
                            <Icon name="Search" size={32} color="var(--color-gray-400)" />
                            <p>Aucun cluster trouvé</p>
                            {mergeClusterSearchTerm && (
                              <small>Essayez un autre terme de recherche</small>
                            )}
                          </div>
                        );
                      }

                      return filteredClusters.map((targetCluster) => {
                        const isSelected = selectedMergeClusters.has(targetCluster.mainKeyword);
                        return (
                          <label
                            key={targetCluster.mainKeyword}
                            className={`cluster-selection-item cluster-checkbox-item ${isSelected ? 'selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                const newSelected = new Set(selectedMergeClusters);
                                if (e.target.checked) {
                                  newSelected.add(targetCluster.mainKeyword);
                                } else {
                                  newSelected.delete(targetCluster.mainKeyword);
                                }
                                setSelectedMergeClusters(newSelected);
                              }}
                            />
                            <div className="cluster-selection-main">
                              <strong>{targetCluster.mainKeyword}</strong>
                              <span className="cluster-selection-count">({targetCluster.keywordCount} mots-clés)</span>
                            </div>
                            <div className="cluster-selection-volume">
                              {formatNumber(targetCluster.totalVolume)} vol.
                            </div>
                          </label>
                        );
                      });
                    })()}
                  </div>
                  {selectedMergeClusters.size > 0 && (
                    <div className="modal-merge-actions">
                      <button
                        className="btn btn-primary btn-merge-confirm"
                        onClick={() => mergeClusters(
                          mergeClusterModal.clusterMainKeyword,
                          Array.from(selectedMergeClusters)
                        )}
                      >
                        <Icon name="Merge" size={16} />
                        Fusionner {selectedMergeClusters.size} cluster{selectedMergeClusters.size > 1 ? 's' : ''}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {createClusterFromOrphanModal && (
            <div 
              className="modal-overlay" 
              onClick={() => setCreateClusterFromOrphanModal(null)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="orphan-cluster-modal-title"
            >
              <div className="modal-content modal-orphan-cluster" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3 id="orphan-cluster-modal-title">
                    {createClusterFromOrphanModal.action === 'create' 
                      ? 'Grouper des mots-clés' 
                      : 'Déplacer vers un cluster'}
                  </h3>
                  <button 
                    className="btn-close-modal"
                    onClick={() => setCreateClusterFromOrphanModal(null)}
                    aria-label="Fermer"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
                <div className="modal-body">
                  {createClusterFromOrphanModal.action === 'create' ? (
                    <>
                      <p className="orphan-cluster-info">
                        Sélectionnez un ou plusieurs mots-clés pour créer un cluster. Le mot-clé avec le plus gros volume sera défini comme mot-clé principal.
                      </p>
                      <div className="selected-keywords-summary">
                        <strong>{selectedKeywordsForCluster.length}</strong> mot(s)-clé(s) sélectionné(s)
                      </div>
                      <div className="orphan-cluster-search-container">
                        <input
                          type="text"
                          className="orphan-cluster-search"
                          placeholder="Rechercher un mot-clé..."
                          value={orphanClusterSearchTerm}
                          onChange={(e) => setOrphanClusterSearchTerm(e.target.value)}
                          autoFocus
                        />
                        {orphanClusterSearchTerm && (
                          <button
                            className="clear-search-btn"
                            onClick={() => setOrphanClusterSearchTerm('')}
                            aria-label="Effacer la recherche"
                          >
                            <Icon name="X" size={16} />
                          </button>
                        )}
                      </div>
                      <div className="orphan-keyword-selection-list">
                        {(() => {
                          const availableKeywords = keywords.filter(k => 
                            !clusteredKeywords.has(k.keyword) &&
                            k.keyword.toLowerCase().includes(orphanClusterSearchTerm.toLowerCase())
                          ).slice(0, 100);

                          if (availableKeywords.length === 0) {
                            return (
                              <div className="no-keywords-found">
                                <Icon name="Search" size={32} color="var(--color-gray-400)" />
                                <p>Aucun mot-clé disponible</p>
                                {orphanClusterSearchTerm && (
                                  <small>Essayez un autre terme de recherche</small>
                                )}
                              </div>
                            );
                          }

                          return availableKeywords.map((kw) => {
                            const isSelected = selectedKeywordsForCluster.includes(kw.keyword);
                            return (
                              <label
                                key={kw.keyword}
                                className={`keyword-selection-item-checkbox ${isSelected ? 'selected' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleKeywordSelection(kw.keyword)}
                                />
                                <div className="keyword-selection-main">
                                  <strong>{kw.keyword}</strong>
                                </div>
                                <div className="keyword-selection-volume">
                                  {formatNumber(kw.volume)} vol.
                                </div>
                              </label>
                            );
                          });
                        })()}
                      </div>
                      <div className="modal-footer">
                        <button
                          className="btn-cancel"
                          onClick={() => setCreateClusterFromOrphanModal(null)}
                        >
                          Annuler
                        </button>
                        <button
                          className="btn-create-cluster-confirm"
                          onClick={createClusterFromMultipleKeywords}
                          disabled={selectedKeywordsForCluster.length < 2}
                        >
                          <Icon name="Plus" size={16} />
                          Grouper ({selectedKeywordsForCluster.length} mots-clés)
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="orphan-cluster-info">
                        Déplacer <strong>{createClusterFromOrphanModal.orphanKeyword}</strong> vers un cluster existant :
                      </p>
                      <div className="orphan-cluster-search-container">
                        <input
                          type="text"
                          className="orphan-cluster-search"
                          placeholder="Rechercher un cluster..."
                          value={orphanClusterSearchTerm}
                          onChange={(e) => setOrphanClusterSearchTerm(e.target.value)}
                          autoFocus
                        />
                        {orphanClusterSearchTerm && (
                          <button
                            className="clear-search-btn"
                            onClick={() => setOrphanClusterSearchTerm('')}
                            aria-label="Effacer la recherche"
                          >
                            <Icon name="X" size={16} />
                          </button>
                        )}
                      </div>
                      <div className="clusters-selection-list">
                        {(() => {
                          const filteredClusters = clusters.filter(c => 
                            c.mainKeyword.toLowerCase().includes(orphanClusterSearchTerm.toLowerCase())
                          );

                          if (filteredClusters.length === 0) {
                            return (
                              <div className="no-clusters-found">
                                <Icon name="Search" size={32} color="var(--color-gray-400)" />
                                <p>Aucun cluster trouvé</p>
                                {orphanClusterSearchTerm && (
                                  <small>Essayez un autre terme de recherche</small>
                                )}
                              </div>
                            );
                          }

                          return filteredClusters.map((targetCluster) => (
                            <button
                              key={targetCluster.mainKeyword}
                              className="cluster-selection-item"
                              onClick={() => addOrphanToExistingCluster(
                                createClusterFromOrphanModal.orphanKeyword,
                                targetCluster.mainKeyword
                              )}
                            >
                              <div className="cluster-selection-main">
                                <strong>{targetCluster.mainKeyword}</strong>
                                <span className="cluster-selection-count">({targetCluster.keywordCount} mots-clés)</span>
                              </div>
                              <div className="cluster-selection-volume">
                                {formatNumber(targetCluster.totalVolume)} vol.
                              </div>
                            </button>
                          ));
                        })()}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeView === "optimization" && (
        <OptimizationView
          key={`optimization-${studyId}`}
          studyId={studyId}
          keywords={keywords}
          activeFilters={activeFilters}
          regexFilters={regexFilters}
        />
      )}
    </div>
  );
};

export default SimilaritiesTable;
